import React, { useState, useEffect } from 'react'
import { Layout, Menu, theme, Avatar, Dropdown, Space, Badge, Button, Drawer } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  DashboardOutlined,
  TransactionOutlined,
  SecurityScanOutlined,
  SafetyOutlined,
  FileTextOutlined,
  AlertOutlined,
  SettingOutlined,
  BarChartOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  HistoryOutlined,
  ShoppingCartOutlined,
  FundOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useIsMobile } from '@hooks/useBreakpoint'

const { Header, Sider, Content } = Layout

interface AppLayoutProps {
  children: React.ReactNode
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const {
    token: { colorBgContainer },
  } = theme.useToken()

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true)
    }
  }, [isMobile])

  const menuItems: MenuProps['items'] = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表板' },
    { key: '/trading', icon: <TransactionOutlined />, label: '交易管理' },
    { key: '/manual-orders', icon: <ShoppingCartOutlined />, label: '主动下单' },
    { key: '/trade-history', icon: <HistoryOutlined />, label: '交易历史' },
    { key: '/risk', icon: <SecurityScanOutlined />, label: '风控管理' },
    { key: '/tokens', icon: <SafetyOutlined />, label: 'Token管理' },
    { key: '/analytics', icon: <BarChartOutlined />, label: '数据分析' },
    { key: '/cfd-stats', icon: <FundOutlined />, label: 'CFD盈亏统计' },
    { key: '/logs', icon: <FileTextOutlined />, label: '日志监控' },
    { key: '/alerts', icon: <AlertOutlined />, label: '异常告警' },
    { key: '/reports', icon: <BarChartOutlined />, label: '报告分析' },
    { key: '/risk-config', icon: <SafetyOutlined />, label: '风控配置' },
    { key: '/dynamic-risk', icon: <SecurityScanOutlined />, label: '动态风控' },
    { key: '/settings', icon: <SettingOutlined />, label: '系统设置' },
  ]

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: '个人资料' },
    { key: 'help', icon: <QuestionCircleOutlined />, label: '帮助文档' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ]

  const currentPageLabel =
    (menuItems.find((item) => item && item.key === location.pathname) as { label?: string })
      ?.label || '系统管理'

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
    if (isMobile) {
      setMobileNavOpen(false)
    }
  }

  const handleUserMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'logout':
        localStorage.removeItem('token')
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_info')
        window.location.href = '/login'
        break
      case 'help':
        window.open('/docs', '_blank')
        break
      default:
        break
    }
  }

  const getUserInfo = () => {
    try {
      const userInfo = localStorage.getItem('user_info')
      return userInfo ? JSON.parse(userInfo) : { username: '管理员' }
    } catch {
      return { username: '管理员' }
    }
  }

  const currentUser = getUserInfo()

  const sideMenu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={handleMenuClick}
      style={{ borderRight: 0 }}
    />
  )

  const siderWidth = collapsed ? 80 : 256

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={256}
          collapsedWidth={80}
          className="app-sider"
        >
          <div className="app-sider-logo">{collapsed ? 'TBP' : 'TradingBridge Pro'}</div>
          {sideMenu}
        </Sider>
      )}

      {isMobile && (
        <Drawer
          title="TradingBridge Pro"
          placement="left"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          width={280}
          styles={{ body: { padding: 0, background: '#001529' } }}
          className="app-mobile-nav-drawer"
        >
          <div className="app-sider-logo app-sider-logo--drawer">TradingBridge Pro</div>
          {sideMenu}
        </Drawer>
      )}

      <Layout
        className="app-main-layout"
        style={{ marginLeft: isMobile ? 0 : siderWidth }}
      >
        <Header className="app-header" style={{ background: colorBgContainer }}>
          <Space>
            <Button
              type="text"
              icon={
                isMobile ? (
                  <MenuUnfoldOutlined />
                ) : collapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() => {
                if (isMobile) {
                  setMobileNavOpen(true)
                } else {
                  setCollapsed(!collapsed)
                }
              }}
              className="app-header-menu-btn"
            />
            <span className="app-header-title">{currentPageLabel}</span>
          </Space>

          <Space size={isMobile ? 8 : 16}>
            <Badge count={3} size="small">
              <Button type="text" icon={<BellOutlined />} className="app-header-icon-btn" />
            </Badge>
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer' }} size={4}>
                <Avatar size="small" icon={<UserOutlined />} />
                {!isMobile && <span>{currentUser.username}</span>}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          className="app-content app-page-container"
          style={{ background: colorBgContainer }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default AppLayout
